/* eslint-disable */
// Kevin R: Too many lint issues to fix them all. Also a high probability of introducing
// bugs while trying to fix them
import { difference, areArraysEqual, intersection, uniq } from '../arrays'
import { capitalize } from '../strings'
import { flip, downcaseObjectKeys } from '../objects'
import { toParam } from './sorting/toParam'
import wrapSearchValueInQuotesIfNeeded from './wrapSearchValueInQuotesIfNeeded'

// Reorder key values arrays into a predicable order, based on internal Groove
// keys. IE should have been passed to _searchOperatorAliasToInternal and not
// have been passed to _searchOperatorAliasToExternal before sorting.
const _orderKeyValuesArray = function(keyValues) {
  const sortKey = [
    'mailbox',
    'assignee',
    'customer',
    'unassigned',
    'assigned',
    'assigned_group',
    'group_unassigned',
    'state',
    'label',
    'mentions',
  ]
  return keyValues
    .sort((a, b) => {
      const aKeyIndex = sortKey.indexOf(a[0])
      const bKeyIndex = sortKey.indexOf(b[0])
      if (bKeyIndex < 0 || aKeyIndex < bKeyIndex) return -1
      if (aKeyIndex < 0 || aKeyIndex > bKeyIndex) return 1
      return 0
    })
    .map(keyValue => {
      const key = keyValue[0]
      return [key].concat(keyValue.slice(1).sort())
    })
}

// Split a query string into parts
// A part is a operator:value pair, or a keyword
// They are separated by spaces (that aren't in quotes), and a double space can
// indicate an empty part.
// Values and keywords can be quoted with ' or "
export function _splitQueryString(queryString) {
  if (!queryString) return null
  const matches = queryString.match(
    /(?:[^\s"]+|(?:")[^"]*(?:"|$))+|(\s(?=\s|$))|(^\s+)/g
  )
  const r = matches && matches.map(x => (x.match(/^\s+$/) ? '' : x))
  return r
}

// Process a query string part into an object with keys and values
export function _processQueryStringPart(queryStringPart) {
  if (queryStringPart.match(/^[":]{1}$/)) {
    return { key: 'keywords', values: [queryStringPart] }
  }
  if (queryStringPart === '') return {}
  const matches = queryStringPart
    .match(/(?:[^:"]+|(?:")[^"]*(?:"))+/g)
    .map(value => {
      const quotedValue = value.match(/^(?:")(.+)(?:")$/)
      return (quotedValue && quotedValue[1]) || value
    })
  if (matches.length === 1 && !queryStringPart.match(/:$/)) {
    return { key: 'keywords', values: matches }
  }
  const key = matches.shift()
  return { key, values: matches }
}

function _flattenKeywordOperations(operations) {
  const newOperations = []
  let foundKeywords = []
  operations.forEach(operation => {
    if (operation.key == 'keywords') {
      foundKeywords = foundKeywords.concat(
        operation.values.map(wrapSearchValueInQuotesIfNeeded)
      )
    } else {
      newOperations.push(operation)
    }
  })
  if (foundKeywords.length > 0) {
    newOperations.push({ key: 'keywords', values: foundKeywords })
  }
  return newOperations
}

// Process query string into an array of key, values operation objects.
function _processQueryString(queryString) {
  if (!queryString) return []
  if (queryString instanceof Array) return queryString
  const queryParts = _splitQueryString(queryString)
  const keyValues = (queryParts || []).map(_processQueryStringPart)
  return _flattenKeywordOperations(keyValues)
}

// Process query object into an array of key, values operation objects.
function _processQueryObject(queryObject) {
  if (!queryObject) return []
  return Object.keys(queryObject).map(key => {
    let values = queryObject[key]
    if (!(values instanceof Array)) values = [values]
    return { key, values }
  }, {})
}

// Often the first step in any search processing so works on all three formats:
//  * search query string - delegates to _processQueryString
//  * search query object - delegates to _processQueryObject
//  * key, value operations array - early return of input
//  * falsey value ("", null, undefined) - early return of an empty array
// Returns an array of object each of which has the following key, value pairs:
//  * key - the left hand part of a "key:value" search operator
//  * values - an array of values, the right hand part of a "key:value" search operator
function _processToOperations(incoming) {
  if (!incoming) return []
  if (incoming instanceof Array) return incoming
  if (typeof incoming === 'string') return _processQueryString(incoming)
  return _processQueryObject(incoming)
}

// Join array of key, value operations objects into a query string.
function _constructQueryString(operationsArray, incomplete) {
  let queryString = operationsArray
    .map(operation => {
      const { key, values } = operation
      if (!key || !values) return null
      return values
        .map(value => {
          if (!value) return null

          const keyString = key === 'keywords' ? '' : `${key}:`
          return `${keyString}${wrapSearchValueInQuotesIfNeeded(value)}`
        })
        .filter(x => !!x)
        .join(' ')
    })
    .filter(x => !!x)
    .join(' ')
  if (incomplete) {
    if (incomplete === true) {
      queryString = `${queryString} `
    } else {
      queryString = `${queryString} ${incomplete}:`
    }
  }
  return queryString
}

// Join array of key, value operation objects into a query object for the store.
function _constructQueryObject(operationsArray) {
  return operationsArray.reduce((queryObject, operation) => {
    queryObject[operation.key] = operation.values
    return queryObject
  }, {})
}

// Replace search operators with internal facing terminology.
// It seems a bit awkward to transform these to strings and then back, but
// at this point regexes are the easiest way to define our aliases.
function _aliasForObject(operationsArray) {
  let query = _constructQueryString(operationsArray)
  query = query.replace(
    /(^|\s)(my\:closed)(?=\s|$)/g,
    '$1state:closed assignee:me'
  )
  query = query.replace(
    /(^|\s)(my\:snoozed)(?=\s|$)/g,
    '$1state:snoozed assignee:me'
  )
  query = query.replace(
    /(^|\s)(my\:open)(?=\s|$)/g,
    '$1state:opened assignee:me'
  )
  query = query.replace(
    /(^|\s)(my\:starred)(?=\s|$)/g,
    '$1starred:all assignee:me'
  )
  query = query.replace(
    /(^|\s)(my\:deleted)(?=\s|$)/g,
    '$1deleted:all assignee:me'
  )
  query = query.replace(
    /(^|\s)(unassigned:[^\s]+)(?=\s|$)/g,
    '$1assignee:unassigned'
  )
  query = query.replace(
    /(^|\s)(group(-|_)unassigned:[^\s]+)(?=\s|$)/g,
    '$1assigned_group:unassigned'
  )
  query = query.replace(/(^|\s)(assignee|agent)\:@?/g, '$1assignee:')
  query = query.replace(
    /(^|\s)(assignee|agent)\:(any|anyone|noone|unassigned|nobody|anybody)(?=\s|$)/g,
    '$1assignee:unassigned'
  )
  query = query.replace(/(^|\s)(tags|tag|labels)(?=\:)/g, '$1label')
  query = query.replace(/(^|\s)(drafts)(?=\:)/g, '$1draft')
  query = query.replace(/(^|\s)(inbox)(?=\:)/g, '$1mailbox')
  query = query.replace(/(^|\s)(group)(?=\:)/g, '$1assigned_group')
  query = query.replace(
    /(^|\s)(is\:unassigned)(?=\s|$)/g,
    '$1assigned_group:unassigned assignee:unassigned'
  )
  query = query.replace(/(^|\s)(is\:assigned)(?=\s|$)/g, '$1assigned:true')
  query = query.replace(/(^|\s)(is\:closed)(?=\s|$)/g, '$1state:closed')
  query = query.replace(/(^|\s)(is\:snoozed)(?=\s|$)/g, '$1state:snoozed')
  query = query.replace(/(^|\s)(is\:unread)(?=\s|$)/g, '$1state:unread')
  query = query.replace(/(^|\s)(is\:deleted)(?=\s|$)/g, '$1deleted:all')
  query = query.replace(/(^|\s)(is\:spam)(?=\s|$)/g, '$1state:spam')
  query = query.replace(/(^|\s)(is\:rated)(?=\s|$)/g, '$1rating:any')
  query = query.replace(
    /(^|\s)(my\:unread)(?=\s|$)/g,
    '$1state:unread assignee:me'
  )
  query = query.replace(
    /(^|\s)((is|state)\:open(ed)?)(?=\s|$)/g,
    '$1state:opened'
  )
  query = query.replace(/(^|\s)(my\:drafts)(?=\s|$)/g, '$1draft:me')
  query = query.replace(/(^|\s)(is\:starred)(?=\s|$)/g, '$1starred:all')
  return _processToOperations(query)
}

// Replace search operators to external customer facing terminology.
// It seems a bit awkward to transform these to strings and then back, but
// at this point regexes are the easiest way to define our aliases.
// The operationsArray should have already been through _aliasForObject,
// _limitValues, and _orderOperations so we only need to alias from the favored
// object format of the operators.
function _aliasForString(operationsArray) {
  let query = _constructQueryString(operationsArray)
  query = query.replace(/(^|\s)label(?=\:)/g, '$1tag')
  query = query.replace(/(^|\s)drafts(?=\:)/g, '$1draft')
  query = query.replace(/(^|\s)mailbox(?=\:)/g, '$1inbox')
  query = query.replace(
    /(^|\s)assigned_group\:unassigned\sassignee:unassigned(?=\s|$)/g,
    '$1is:unassigned'
  )
  query = query.replace(
    /(^|\s)(unassigned:[^\s]+)(?=\s|$)/g,
    '$1assignee:unassigned'
  )
  query = query.replace(
    /(^|\s)(group(-|_)unassigned:[^\s]+)(?=\s|$)/g,
    '$1group:unassigned'
  )
  query = query.replace(/(^|\s)assigned_group(?=\:)/g, '$1group')
  query = query.replace(
    /(^|\s)assignee\:me state\:closed(?=\s|$)/g,
    '$1my:closed'
  )
  query = query.replace(
    /(^|\s)assignee\:me state\:snoozed(?=\s|$)/g,
    '$1my:snoozed'
  )
  query = query.replace(
    /(^|\s)assignee\:me state\:opened(?=\s|$)/g,
    '$1my:open'
  )
  query = query.replace(
    /(^|\s)assignee\:me state\:unread(?=\s|$)/g,
    '$1my:unread'
  )
  query = query.replace(
    /(^|\s)assignee\:me deleted\:all(?=\s|$)/g,
    '$1my:deleted'
  )
  query = query.replace(
    /(^|\s)assignee\:me starred\:all(?=\s|$)/g,
    '$1my:starred'
  )
  query = query.replace(/(^|\s)assigned\:true?(?=\s|$)/g, '$1is:assigned')
  query = query.replace(/(^|\s)draft\:me?(?=\s|$)/g, '$1my:drafts')
  query = query.replace(/(^|\s)state\:opened?(?=\s|$)/g, '$1is:open')
  query = query.replace(/(^|\s)state\:closed?(?=\s|$)/g, '$1is:closed')
  query = query.replace(/(^|\s)state\:snoozed?(?=\s|$)/g, '$1is:snoozed')
  query = query.replace(/(^|\s)state\:unread?(?=\s|$)/g, '$1is:unread')
  query = query.replace(/(^|\s)state\:spam?(?=\s|$)/g, '$1is:spam')
  query = query.replace(/(^|\s)starred\:all(?=\s|$)/g, '$1is:starred')
  query = query.replace(/(^|\s)deleted\:all?(?=\s|$)/g, '$1is:deleted')
  return _processToOperations(query)
}

export function reverseSearchIdValueMap(idValueMap) {
  return Object.keys(idValueMap).reduce((reversingIdValueMap, key) => {
    reversingIdValueMap[key] = Object.keys(idValueMap[key]).reduce(
      (reversedIdValueSet, id) => {
        const value = idValueMap[key][id]
        reversedIdValueSet[value] = id
        return reversedIdValueSet
      },
      {}
    )
    return reversingIdValueMap
  }, {})
}

// Replace label values for IDs.
// Works on array of key, values operation objects.
function _aliasIdsForObject(operationsArray, aliases = {}) {
  return operationsArray.map(operation => {
    const { key, values } = operation
    const downcasedKey = `__${key}_downcased`
    let aliasSet = {}
    if (aliases[downcasedKey]) {
      aliasSet = aliases[downcasedKey]
    } else if (aliases[key]) {
      aliasSet = downcaseObjectKeys(aliases[key])
      aliases[downcasedKey] = aliasSet
    }
    return {
      key,
      values: values.map(value => {
        return aliasSet[value.toLowerCase()] || value
      }),
    }
  }, {})
}

// Replace ID values for labels.
// Works on array of key, values operation objects.
// Reverses aliases and delegates to _aliasIdsForObject
function _aliasIdsForString(operationsArray, aliases = {}) {
  const reversedAliases = reverseSearchIdValueMap(aliases)
  return _aliasIdsForObject(operationsArray, reversedAliases)
}

// Order operations based on _orderOperationsKeyOrder, then alphabetically. Will
// also combine adjacent operations, order the values alphabetically, and remove
// duplicates.
const _orderOperationsKeyOrder = [
  'mailbox',
  'assigned_group',
  'assignee',
  'assigned',
  'draft',
  'starred',
  'state',
  'label',
  'keywords',
]
function _orderOperations(operationsArray) {
  const sortedOperationsArray = operationsArray.sort((a, b) => {
    const aKey = a.key
    const bKey = b.key
    const aKeyIndex = _orderOperationsKeyOrder.indexOf(aKey)
    const bKeyIndex = _orderOperationsKeyOrder.indexOf(bKey)
    if (aKeyIndex > -1 && bKeyIndex < 0) return -1
    if (bKeyIndex > -1 && aKeyIndex < 0) return 1
    if (aKeyIndex < bKeyIndex) return -1
    if (aKeyIndex > bKeyIndex) return 1
    return aKey < bKey ? -1 : aKey > bKey ? 1 : 0
  })
  let previousOperation = {}
  sortedOperationsArray
    .map(operation => {
      if (previousOperation.key === operation.key) {
        previousOperation.values.concat(operation.values)
        return null
      }
      previousOperation = operation
    })
    .filter(x => !!x)
  sortedOperationsArray.forEach(operation => {
    operation.values = uniq(operation.values).sort()
  })
  return sortedOperationsArray
}

// Limit operator values to one value unless listed in
// _limitValuesMultipleValid.
const _limitValuesMultipleValid = ['keywords']
// One day this may include other operators, labels being an obvious one, but
// will need to be supported by underlying search first.
function _limitValues(operationsArray) {
  const existingKeys = []
  return operationsArray
    .reverse()
    .map(operation => {
      const { key, values } = operation
      if (_limitValuesMultipleValid.indexOf(key) > -1) {
        return operation
      } else if (existingKeys.indexOf(key) > -1) {
        return null
      }
      existingKeys.push(key)
      return { key, values: [values[values.length - 1]] }
    })
    .filter(x => !!x)
    .reverse()
}

// Find and return the requested value from a query
function _findSingleOperatorValue(operationsArray, operator) {
  const operation = operationsArray.find(operation => {
    return operation.key === operator
  })
  return (
    operation &&
    operation.values &&
    operation.values[operation.values.length - 1]
  )
}

// Remove the provided operator from the search
function _ignoreOperator(operationsArray, operator) {
  return operationsArray.filter(operation => {
    return operation.key !== operator
  })
}

// Construct a search query string from any of the following:
//  * search query string - will return with "favored" aliases and order of operators
//  * search query object
export function constructSearchQueryString(
  incomingQueryObject,
  idValueMap = {},
  options = {}
) {
  let query = _processToOperations(incomingQueryObject)
  query = _aliasForObject(query)
  // query = _processQueryString(query)
  query = _limitValues(query)
  query = _orderOperations(query)
  query = _aliasIdsForString(query, idValueMap)
  query = _aliasForString(query)
  query = _constructQueryString(query, options.incomplete)
  return query
}

export function constructEncodedSearchQueryString(
  incomingQueryObject,
  idValueMap = {},
  options = {}
) {
  const { sortOrder } = options
  const encodedQueryString = encodeURIComponent(
    constructSearchQueryString(incomingQueryObject, idValueMap, options)
  )
  if (!sortOrder) return encodedQueryString
  return `${encodedQueryString}${toParam(sortOrder)}`
}

export function deconstructEncodedSearchQueryString(string) {
  return constructSearchQueryObject(decodeURIComponent(string))
}

// Construct a search query ID from any of the following:
//  * search query string
//  * search query object
// This is is very similar to a search query string except IDs are kept inplace
// instead of substituting for labels.
export function constructSearchQueryId(incomingQueryObject, idValueMap = {}) {
  let query = _processToOperations(incomingQueryObject)
  query = _aliasForObject(query)
  // query = _processQueryString(query)
  query = _limitValues(query)
  query = _orderOperations(query)
  query = _aliasIdsForObject(query, idValueMap)
  query = _aliasForString(query)
  query = _constructQueryString(query)
  return query
}

export function constructMailboxLessSearchQueryId(
  incomingQueryObject,
  idValueMap = {}
) {
  const queryObjectCopy = { ...incomingQueryObject }
  delete queryObjectCopy.mailbox
  return constructSearchQueryId(queryObjectCopy, idValueMap)
}

// Construct a search query object for storing in state from any of the following:
//  * search query string
//  * search query object
// This is is very similar to a search query string except IDs are kept inplace
// instead of substituting for labels.
export function constructSearchQueryObject(incomingQuery, idValueMap = {}) {
  let query = _processToOperations(incomingQuery)
  query = _aliasForObject(query)
  // query = _processQueryString(query)
  query = _limitValues(query)
  query = _orderOperations(query)
  query = _aliasIdsForObject(query, idValueMap)
  query = _constructQueryObject(query)
  return query
}

// Construct a search query object mapped to the graphql SeachQuery input type:
export function constructGraphQLSearchQueryObject(
  incomingQuery,
  idValueMap = {}
) {
  const query = constructSearchQueryObject(incomingQuery, idValueMap)

  Object.keys(query).forEach(key => {
    if (key === 'keywords') return // do not modify keywords key
    const currentValue = query[key]
    query[key] = currentValue && currentValue[currentValue.length - 1]
  })
  // search api expects keywords to be a space separated string
  if (query.keywords) {
    query.keywords = query.keywords.join(' ')
  }

  if (query.assignee === 'unassigned') {
    query.unassigned = true
    delete query.assignee
  }

  if (query.assigned_group === 'unassigned') {
    query.group_unassigned = true
    delete query.assigned_group
  }

  if (query.starred === 'all') {
    query.is_starred = true
    delete query.starred
  }

  if (query.mentions) {
    query.mention = query.mentions
    delete query.mentions
  }

  return query
}

// Return preset title for a query, or if there isn't one the query string.
// !IMPORTANT If you add a titled search you must ensure it's query ID is added
// to the server side's TicketFilterDetector so that diff's include it.
const searchQueryTitles = {
  'is:open': 'Open',
  'is:closed': 'Closed',
  'is:snoozed': 'Snoozed',
  'is:unread': 'Unread',
  'is:deleted': 'Deleted',
  'is:spam': 'Spam',
  // !IMPORTANT see comment at top
  'assignee:me': 'All of Mine',
  'my:open': 'Mine',
  'my:closed': 'My Closed',
  'my:snoozed': 'My Snoozed',
  'my:unread': 'My Unread',
  'my:deleted': 'My Deleted',
  // !IMPORTANT see comment at top
  'my:drafts': 'All of My Drafts',
  'my:drafts is:open': 'My Drafts',
  'my:drafts is:closed': 'My Closed Drafts',
  'my:drafts is:snoozed': 'My Snoozed Drafts',
  'my:drafts is:deleted': 'Deleted with My Drafts',
  'my:drafts is:spam': 'Spam with My Drafts',
  // !IMPORTANT see comment at top
  'is:unassigned': 'All Unassigned',
  'is:unassigned is:open': 'Unassigned',
  'is:unassigned is:closed': 'Unassigned Closed',
  'is:unassigned is:snoozed': 'Unassigned Snoozed',
  'is:unassigned is:unread': 'Unassigned Unread',
  'is:unassigned is:deleted': 'Unassigned Deleted',
  'is:unassigned is:spam': 'Unassigned Spam',
  // !IMPORTANT see comment at top
  'is:assigned': 'All Assigned',
  'is:assigned is:open': 'Assigned',
  'is:assigned is:closed': 'Assigned Closed',
  'is:assigned is:snoozed': 'Assigned Snoozed',
  'is:assigned is:unread': 'Assigned Unread',
  'is:assigned is:deleted': 'Assigned Deleted',
  'is:assigned is:spam': 'Assigned Spam',
  // !IMPORTANT see comment at top
  'is:starred': 'All Starred',
  'is:starred is:open': 'Starred',
  'is:starred is:closed': 'Starred Closed',
  'is:starred is:snoozed': 'Starred Snoozed',
  'is:starred is:unread': 'Starred Unread',
  'is:starred is:deleted': 'Starred Deleted',
  'is:starred is:spam': 'Starred Spam',
}

// !IMPORTANT If you add a titled search you must ensure it's query ID is added
// to the server side's TicketFilterDetector so that diff's include it.
export function constructSearchQueryTitle(incomingQuery, idValueMap = {}) {
  const reversedIdValueMap = reverseSearchIdValueMap(idValueMap)
  let query = _processToOperations(incomingQuery)
  query = _aliasForObject(query)
  query = _limitValues(query)
  query = _orderOperations(query)
  query = _aliasIdsForObject(query, idValueMap)

  query = _ignoreOperator(query, 'mailbox') // Query titles are the same regardless of mailbox
  const queryLessMailbox = constructSearchQueryString(query, idValueMap)
  if (searchQueryTitles[queryLessMailbox])
    return searchQueryTitles[queryLessMailbox]
  const state = _findSingleOperatorValue(query, 'state')
  const deleted = _findSingleOperatorValue(query, 'deleted')
  query = _ignoreOperator(query, 'state')
  query = _ignoreOperator(query, 'deleted')
  query = _ignoreOperator(query, 'draft_type')
  const soleOperator = query[0]
  if (query.length === 1 && soleOperator) {
    const value = soleOperator.values[0]
    const valueMapped =
      reversedIdValueMap[soleOperator.key] &&
      reversedIdValueMap[soleOperator.key][value]
    const stateTitle =
      (deleted === 'all' && ' Deleted') ||
      (state && state !== 'opened' ? ` ${capitalize(state)}` : '')
    if (
      value === 'unassigned' &&
      (soleOperator.key === 'assignee' || soleOperator.key === 'assigned_group')
    ) {
      if (state === 'closed') {
        return `Closed Unassigned`
      } else if (state === 'snoozed') {
        return `Snoozed Unassigned`
      } else if (state === undefined) {
        return `All Unassigned`
      }
      return `Unassigned`
    }
    if (soleOperator.key === 'assignee') {
      const all = !state && !deleted ? 'All of ' : ''
      if (state === 'spam' && (valueMapped || value) === 'me') return 'My Spam'
      return `${all}${capitalize(valueMapped || value)}'s${stateTitle}`
    }
    if (soleOperator.key === 'folder') {
      // Show nothing if we only have an ID + folder mappings arent yet loaded
      if (!isNaN(value) && !valueMapped) return ''
      return `${valueMapped || value}`
    }
    if (soleOperator.key === 'assigned_group') {
      const all = !state && !deleted ? 'All in ' : ''
      return `${all}${valueMapped || value}${stateTitle}`
    }
    if (soleOperator.key === 'label') {
      // Show nothing if we only have an ID + label mappings arent yet loaded
      if (!isNaN(value) && !valueMapped) return ''

      const all = !state && !deleted ? 'All ' : ''
      return `${all}${valueMapped || value}${stateTitle}`
    }
    if (soleOperator.key === 'mentions') {
      if (valueMapped === 'me') {
        switch (state) {
          case 'closed':
            return 'My Closed Mentions'
          case 'snoozed':
            return 'My Snoozed Mentions'
          case undefined:
            return 'All My Mentions'
          default:
            return 'My Mentions'
        }
      }

      if (valueMapped) {
        switch (state) {
          case 'closed':
            return `Closed @${valueMapped}`
          case 'snoozed':
            return `Snoozed @${valueMapped}`
          case undefined:
            return `All @${valueMapped}`
          default:
            return `@${valueMapped}`
        }
      }

      return 'Mentions'
    }
    // !IMPORTANT see comment at top
  }
  return null
}

// Compare two search query strings or objects for equivilence.
// EG `stage:opened` and `is:open` are equivilent
export function areSearchesEquivalent(searchA, searchB, idValueMap = {}) {
  const searchAString = constructSearchQueryString(searchA, idValueMap)
  const searchBString = constructSearchQueryString(searchB, idValueMap)
  return searchAString === searchBString
}

//  * have operator values that exactly match each of the corresponding provided values for `options.exactly`
//  * have operator values that contain all of the corresponding provided values for `options.all`
//  * have operator values that contain at least one of the corresponding provided values for `options.any`
//  * have at least one operator value for each of the corresponding provided values for `options.present`
export function filterSearches(searches, options) {
  const { all, any, exactly, present } = options
  const limitToProvided = present['*'] === false && delete present['*']
  return Object.values(searches).filter(search => {
    const providedKeys = []
    const queryObject = search.queryObject

    let allMatch = true
    all &&
      Object.keys(all).forEach(key => {
        providedKeys.push(key)
        let value = all[key]
        const searchValue = queryObject[key]
        if (typeof value === 'string') value = [value]
        allMatch =
          allMatch && searchValue && difference(value, searchValue).length === 0
      })

    let anyMatch = true
    any &&
      Object.keys(any).forEach(key => {
        providedKeys.push(key)
        let value = any[key]
        const searchValue = queryObject[key]
        if (typeof value === 'string') value = [value]
        anyMatch =
          anyMatch && searchValue && intersection(value, searchValue).length > 0
      })

    let exactlyMatch = true
    exactly &&
      Object.keys(exactly).forEach(key => {
        providedKeys.push(key)
        let value = exactly[key]
        const searchValue = queryObject[key]
        if (typeof value === 'string') value = [value]
        exactlyMatch =
          exactlyMatch && searchValue && areArraysEqual(value, searchValue)
      })

    let presentMatch = true
    present &&
      Object.keys(present).forEach(key => {
        const value = present[key]
        if (value) providedKeys.push(key)
        const isPresent =
          queryObject[key] !== null && queryObject[key] !== undefined
        presentMatch = presentMatch && (value ? isPresent : !isPresent)
      })

    const limitToProvidedMatch =
      limitToProvided &&
      difference(Object.keys(queryObject), providedKeys).length === 0

    return (
      allMatch &&
      anyMatch &&
      exactlyMatch &&
      presentMatch &&
      limitToProvidedMatch
    )
  })
}

export function doesTicketMatchSearch(ticket, search) {
  const queryObject = constructSearchQueryObject(search)

  const mailboxMatch = !(
    queryObject.mailbox && queryObject.mailbox.indexOf(ticket.mailboxId) === -1
  )
  const stateMatch = !(
    queryObject.state &&
    !(queryObject.state[0] === 'opened' && ticket.state === 'unread') &&
    // if we are on a snoozed search, the ticket state is probably 'closed', so
    // we have to check the snoozedById field.
    !(
      queryObject.state[0] === 'snoozed' &&
      (ticket.snoozedUntil || ticket.snoozedById)
    ) &&
    queryObject.state.indexOf(ticket.state) === -1
  )
  const labelMatch = !(
    queryObject.label &&
    difference(queryObject.label, ticket.labelIds).length > 0
  )
  let assigneeMatch
  if (
    queryObject.assignee &&
    queryObject.assignee[0] == 'unassigned' &&
    !ticket.assignee
  ) {
    assigneeMatch = true
  } else {
    assigneeMatch = !(
      queryObject.assignee &&
      (!ticket.assignee ||
        (ticket.assignee &&
          queryObject.assignee.indexOf(ticket.assignee.id) === -1))
    )
  }

  let assignedGroupMatch
  if (
    queryObject.assigned_group &&
    queryObject.assigned_group[0] == 'unassigned' &&
    !ticket.assigned_group_id
  ) {
    assignedGroupMatch = true
  } else {
    assignedGroupMatch = !(
      queryObject.assigned_group &&
      (!ticket.assigned_group_id ||
        queryObject.assigned_group.indexOf(ticket.assigned_group_id) === -1)
    )
  }

  const starredMatch = !(
    queryObject.starred &&
    queryObject.starred[0] === 'all' &&
    ticket.priority !== 'urgent'
  )
  const deletedFolderMatch = !(
    queryObject.deleted &&
    queryObject.deleted[0] === 'all' &&
    !ticket.deleted_at
  )
  const deletedMatch = !(!queryObject.deleted && ticket.deleted_at)
  return (
    mailboxMatch &&
    stateMatch &&
    labelMatch &&
    assigneeMatch &&
    assignedGroupMatch &&
    starredMatch &&
    deletedFolderMatch &&
    deletedMatch
  )
}

export function searchDiff(ticket, search) {
  return {
    searches: {
      [constructSearchQueryId(search)]: doesTicketMatchSearch(ticket, search)
        ? 0
        : -1,
    },
  }
}

export function mergeSearchQueryObject(query1, query2, idValueMap = {}) {
  const queries = [query1, query2]
  const newQueryObject = {}
  queries.forEach(query => {
    const queryObject = constructSearchQueryObject(query, idValueMap)
    const keys = Object.keys(queryObject).concat(Object.keys(newQueryObject))
    uniq(keys).forEach(key => {
      if (!newQueryObject[key]) newQueryObject[key] = []
      newQueryObject[key] = newQueryObject[key].concat(queryObject[key] || [])
      // Null operator values are stripped, but here it mean reset the operator
      if (query[key] === null) newQueryObject[key] = []
    })
  })
  return newQueryObject
}

export function mergeSearchQueryString(query1, query2, idValueMap = {}) {
  const queryObject = mergeSearchQueryObject(query1, query2, idValueMap)
  return constructSearchQueryString(queryObject, idValueMap)
}

export function mergeEncodedSearchQueryString(query1, query2, idValueMap = {}) {
  const queryObject = mergeSearchQueryObject(query1, query2, idValueMap)
  return constructEncodedSearchQueryString(queryObject, idValueMap)
}

export function searchOpenTicketsPath(
  agentId,
  groupId,
  queryValueMap,
  mailboxName = null
) {
  let assignee = agentId
  let assignedGroup = groupId

  // This is for 'Unassigned' option to return 'is:unassigned is:open'
  if (assignee === null) assignee = 'unassigned'
  if (assignedGroup === null) assignedGroup = 'unassigned'

  const queryObject = {
    assignee,
    assigned_group: assignedGroup,
    state: ['opened'],
    mailbox: mailboxName ? mailboxName.toLowerCase() : null,
  }
  const string = constructEncodedSearchQueryString(queryObject, queryValueMap)
  return `/search/${string}`
}

export const normalizeDiffSearchesPayload = payload => {
  let key
  const obj = {}
  for (key in payload) {
    const value = payload[key]
    const newKey = constructSearchQueryId(key)
    obj[newKey] = value
  }
  return obj
}

export const addMailboxToQueryString = (string, mailboxName) => {
  const queryObject = constructSearchQueryObject(string)
  if (!queryObject.mailbox) {
    queryObject.mailbox = [mailboxName]
  }
  return constructSearchQueryId(queryObject)
}

// Shorthand way of building links. Includes the given mailbox, search order,
// and state.
export const buildSearchPath = ({
  mailboxName,
  sortOrder,
  state,
}) => queryObj =>
  `/search/${constructEncodedSearchQueryString(
    {
      state,
      mailbox: mailboxName ? mailboxName.toLowerCase() : null,
      ...queryObj,
    },
    {},
    { sortOrder }
  )}`

// buildSearchPath, but takes a query string e.g. 'my:open'
export const buildSearchPathFromId = options => string => {
  const queryObject = constructSearchQueryObject(string)
  return buildSearchPath(options)(queryObject)
}
